import React, { useCallback, useEffect, useState } from 'react'
import Container from '../layout/Container'
import RootStore from '../../store/Root'
import {
    getEcsuiteCardRecoveryEmailBatch,
    getEcsuiteCardRecoveryQueue,
    previewEcsuiteCardRecoveryEmails,
    sendAllEcsuiteCardRecoveryEmails,
    sendEcsuiteCardRecoveryEmail
} from '../../api/EcsuiteCardRecovery'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faEnvelope, faPaperPlane, faRotate, faShieldHalved, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons'
import './StickyIoCardRecovery.css'

interface Props {
    rootStore: RootStore
}

interface RecoveryRow {
    recovery_reference: string
    customer_reference: string
    domain: string
    status: string
    failure_count: number
    affected_purchase_count: number
    purchase_types: string[]
    nominal_amount: number
    first_failure_utc: string
    last_failure_utc: string
    refreshed_at_utc: string | null
    customer_refresh_url: string | null
    email_status: 'not_sent' | 'sending' | 'sent' | 'failed'
    email_sent_at_utc: string | null
}

interface RecoverySummary {
    total: number
    failed_attempts: number
    nominal_amount: number
    needs_refresh: number
    review_required: number
}

const defaultSummary: RecoverySummary = {
    total: 0,
    failed_attempts: 0,
    nominal_amount: 0,
    needs_refresh: 0,
    review_required: 0
}

interface EmailPreview {
    eligible_customers: number
    ready_to_send: number
    already_sent: number
    in_progress: number
    ineligible_or_duplicate_references: number
}

interface EmailBatch {
    status: string
    total: number
    processed: number
    sent: number
    already_sent: number
    failed: number
}

const statusLabels: Record<string, string> = {
    needs_refresh: 'Needs customer refresh',
    refresh_completed: 'Refresh completed',
    later_payment_succeeded: 'Later payment succeeded',
    reference_replaced: 'Reference replaced',
    no_active_card: 'No active card',
    review_required: 'Review required'
}

const purchaseLabels: Record<string, string> = {
    feed_unlock: 'Feed unlock',
    chat_unlock: 'Chat unlock',
    subscription: 'Subscription',
    resubscription: 'Resubscription',
    tip: 'Tip',
    shop: 'Shop purchase'
}

const formatDate = (value: string) => new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC'
}).format(new Date(value))

const formatMoney = (value: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
}).format(value || 0)

const StickyIoCardRecovery: React.FC<Props> = ({ rootStore }) => {
    const [days, setDays] = useState(90)
    const [domain, setDomain] = useState('')
    const [status, setStatus] = useState('all')
    const [page, setPage] = useState(1)
    const [rows, setRows] = useState<RecoveryRow[]>([])
    const [summary, setSummary] = useState<RecoverySummary>(defaultSummary)
    const [totalRows, setTotalRows] = useState(0)
    const [totalPages, setTotalPages] = useState(1)
    const [checkedAt, setCheckedAt] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [sendingReference, setSendingReference] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [emailPreview, setEmailPreview] = useState<EmailPreview | null>(null)
    const [isPreviewing, setIsPreviewing] = useState(false)
    const [isStartingBatch, setIsStartingBatch] = useState(false)
    const [emailBatch, setEmailBatch] = useState<EmailBatch | null>(null)
    const [batchId, setBatchId] = useState('')
    const canSendEmail = rootStore.authStore.userRole === 'SUPER_ADMIN'

    const runScan = useCallback(async (requestedPage = page) => {
        setIsLoading(true)
        setErrorMessage('')
        try {
            const response = await getEcsuiteCardRecoveryQueue({
                days,
                domain: domain.trim(),
                status,
                page: requestedPage,
                page_size: 25
            })
            setRows(response.data.rows || [])
            setSummary(response.data.summary || defaultSummary)
            setTotalRows(response.data.totalRows || 0)
            setTotalPages(response.data.totalPages || 1)
            setPage(response.data.currentPage || 1)
            setCheckedAt(response.data.checked_at_utc || '')
        } catch (error) {
            setErrorMessage('The recovery scan could not be completed. No payment or card was changed.')
        } finally {
            setIsLoading(false)
        }
    }, [days, domain, page, status])

    useEffect(() => {
        if (!batchId) return
        let cancelled = false
        let timer: number | undefined
        const poll = async () => {
            try {
                const response = await getEcsuiteCardRecoveryEmailBatch(batchId)
                if (cancelled) return
                const batch = response.data.batch as EmailBatch
                setEmailBatch(batch)
                if (['completed', 'completed_with_failures', 'failed'].includes(batch.status)) {
                    setBatchId('')
                    setSuccessMessage(batch.failed > 0
                        ? `Email run finished: ${batch.sent + batch.already_sent} delivered or previously sent, ${batch.failed} failed.`
                        : `Email run finished: ${batch.sent + batch.already_sent} customers covered.`)
                    runScan(1)
                    return
                }
                timer = window.setTimeout(poll, 2000)
            } catch (error) {
                if (!cancelled) setErrorMessage('Email delivery is continuing, but its progress could not be refreshed.')
            }
        }
        poll()
        return () => {
            cancelled = true
            if (timer) window.clearTimeout(timer)
        }
    }, [batchId])

    useEffect(() => {
        runScan(1)
        // The initial scan intentionally runs once with the safe default window.
    }, [])

    const applyFilters = () => {
        setPage(1)
        runScan(1)
    }

    const changePage = (nextPage: number) => {
        if (nextPage < 1 || nextPage > totalPages || isLoading) return
        setPage(nextPage)
        runScan(nextPage)
    }

    const sendRefreshEmail = async (row: RecoveryRow) => {
        setSendingReference(row.recovery_reference)
        setErrorMessage('')
        setSuccessMessage('')
        try {
            const response = await sendEcsuiteCardRecoveryEmail({
                days,
                domain: row.domain,
                recovery_reference: row.recovery_reference
            })
            setSuccessMessage(response.data.delivery_status === 'already_sent'
                ? 'That customer had already received the refresh email.'
                : 'Refresh email sent successfully from the website.')
            runScan(page)
        } catch (error) {
            setErrorMessage('The refresh email was not confirmed as sent. No payment or card was changed.')
        } finally {
            setSendingReference('')
        }
    }

    const openSendAllReview = async () => {
        setIsPreviewing(true)
        setErrorMessage('')
        setSuccessMessage('')
        try {
            const response = await previewEcsuiteCardRecoveryEmails({ days, domain: domain.trim() })
            setEmailPreview(response.data)
        } catch (error) {
            setErrorMessage('The bulk email review could not be prepared. Nothing was sent.')
        } finally {
            setIsPreviewing(false)
        }
    }

    const confirmSendAll = async () => {
        if (!emailPreview) return
        setIsStartingBatch(true)
        setErrorMessage('')
        try {
            const response = await sendAllEcsuiteCardRecoveryEmails({
                days,
                domain: domain.trim(),
                expected_ready_count: emailPreview.ready_to_send
            })
            setEmailPreview(null)
            if (!response.data.batch_id) {
                setSuccessMessage('No unsent recovery emails remain for these filters.')
                runScan(1)
                return
            }
            setEmailBatch({
                status: response.data.status,
                total: response.data.total,
                processed: 0,
                sent: 0,
                already_sent: 0,
                failed: 0
            })
            setBatchId(response.data.batch_id)
        } catch (error) {
            setErrorMessage('The email run could not be started. Nothing new was queued.')
        } finally {
            setIsStartingBatch(false)
        }
    }

    const pendingCount = (summary.needs_refresh || 0) + (summary.review_required || 0)

    return <Container rootStore={rootStore} redirectIfNotLoggedIn={true}>
        <div className='ecsuite-recovery-page'>
            <div className='card'>
                <div className='card-body'>
                    <div className='ecsuite-recovery-heading'>
                        <div>
                            <h1>ECSuite Card Recovery</h1>
                            <p>Find saved-card references affected by the NMI initial transaction error across PCP websites.</p>
                        </div>
                        {checkedAt && <small className='text-muted'>Last scanned {formatDate(checkedAt)} UTC</small>}
                    </div>

                    <div className='ecsuite-recovery-safety mt-3' role='note'>
                        <FontAwesomeIcon icon={faShieldHalved} aria-hidden='true' />
                        <div>
                            <strong>Payment-safe recovery</strong>
                            <div>Emails only ask customers to refresh their own saved card. This page never charges, retries, deletes, or edits a card.</div>
                        </div>
                    </div>

                    <div className='ecsuite-recovery-summary mt-3'>
                        <div className='ecsuite-recovery-stat'><span>Pending recovery</span><strong>{pendingCount}</strong></div>
                        <div className='ecsuite-recovery-stat'><span>Affected references</span><strong>{summary.total || 0}</strong></div>
                        <div className='ecsuite-recovery-stat'><span>Failed attempts</span><strong>{summary.failed_attempts || 0}</strong></div>
                        <div className='ecsuite-recovery-stat'><span>Nominal value</span><strong>{formatMoney(summary.nominal_amount)}</strong></div>
                    </div>

                    <div className='ecsuite-recovery-toolbar mt-4'>
                        <div>
                            <label htmlFor='recovery-domain'>Website domain</label>
                            <input
                                id='recovery-domain'
                                className='form-control'
                                value={domain}
                                onChange={(event) => setDomain(event.target.value)}
                                placeholder='All PCP websites'
                            />
                        </div>
                        <div>
                            <label htmlFor='recovery-window'>History</label>
                            <select id='recovery-window' className='form-select' value={days} onChange={(event) => setDays(Number(event.target.value))}>
                                <option value={30}>Last 30 days</option>
                                <option value={90}>Last 90 days</option>
                                <option value={180}>Last 180 days</option>
                                <option value={365}>Last 365 days</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor='recovery-status'>Recovery status</label>
                            <select id='recovery-status' className='form-select' value={status} onChange={(event) => setStatus(event.target.value)}>
                                <option value='all'>All statuses</option>
                                {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </select>
                        </div>
                        <button type='button' className='btn btn-primary ecsuite-recovery-scan-button' onClick={applyFilters} disabled={isLoading}>
                            <FontAwesomeIcon className={isLoading ? 'rotate' : ''} icon={faRotate} aria-hidden='true' />
                            {isLoading ? 'Scanning…' : 'Scan records'}
                        </button>
                    </div>

                    {errorMessage && <div className='alert alert-danger mt-3 mb-0' role='alert'>{errorMessage}</div>}
                    {successMessage && <div className='alert alert-success mt-3 mb-0' role='status'>{successMessage}</div>}
                    {emailBatch && batchId && <div className='ecsuite-recovery-progress mt-3' role='status'>
                        <div>
                            <strong>Sending recovery emails</strong>
                            <span>{emailBatch.processed} of {emailBatch.total} customers processed</span>
                        </div>
                        <progress className='ecsuite-recovery-progress-bar' aria-label='Recovery email delivery progress' value={emailBatch.processed} max={emailBatch.total || 1} />
                    </div>}
                </div>
            </div>

            <div className='card mt-4'>
                <div className='card-header ecsuite-recovery-queue-header'>
                    <div>
                        <span>Recovery queue</span>
                        <span className='badge text-bg-secondary'>{totalRows} references</span>
                    </div>
                    {canSendEmail && <button type='button' className='btn btn-sm btn-primary ecsuite-recovery-send-all' onClick={openSendAllReview} disabled={isLoading || isPreviewing || Boolean(batchId)}>
                        <FontAwesomeIcon icon={faPaperPlane} aria-hidden='true' />
                        {isPreviewing ? 'Reviewing…' : 'Send all eligible'}
                    </button>}
                </div>
                <div className='card-body p-0'>
                    <div className='table-responsive'>
                        <table className='table table-hover mb-0 ecsuite-recovery-table'>
                            <thead>
                                <tr>
                                    <th>Website / customer</th>
                                    <th>Status</th>
                                    <th>Failures</th>
                                    <th>Purchase types</th>
                                    <th>Nominal value</th>
                                    <th>Last failure</th>
                                    <th><span className='visually-hidden'>Action</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {!isLoading && rows.length === 0 && <tr><td colSpan={7} className='ecsuite-recovery-empty'>No affected saved-card references match these filters.</td></tr>}
                                {rows.map((row) => <tr key={row.recovery_reference}>
                                    <td>
                                        <span className='ecsuite-recovery-domain'>{row.domain}</span>
                                        <span className='ecsuite-recovery-reference'>Customer {row.customer_reference}</span>
                                    </td>
                                    <td><span className={`ecsuite-recovery-status ecsuite-recovery-status--${row.status}`}>{statusLabels[row.status] || row.status}</span></td>
                                    <td>{row.failure_count}<span className='ecsuite-recovery-types d-block'>{row.affected_purchase_count} purchases</span></td>
                                    <td className='ecsuite-recovery-types'>{row.purchase_types.map((type) => purchaseLabels[type] || type).join(', ') || 'Unknown'}</td>
                                    <td>{formatMoney(row.nominal_amount)}</td>
                                    <td>{formatDate(row.last_failure_utc)}<span className='ecsuite-recovery-types d-block'>UTC</span></td>
                                    <td>
                                        {canSendEmail && row.customer_refresh_url && row.status === 'needs_refresh' && row.email_status !== 'sent' && <button
                                            type='button'
                                            className='btn btn-sm btn-outline-primary ecsuite-recovery-action'
                                            onClick={() => sendRefreshEmail(row)}
                                            disabled={sendingReference === row.recovery_reference || row.email_status === 'sending'}
                                            title='Send a secure card-refresh email from this website'
                                            aria-label={`Send refresh email to customer ${row.customer_reference}`}
                                        >
                                            <FontAwesomeIcon icon={faEnvelope} aria-hidden='true' />
                                            {sendingReference === row.recovery_reference || row.email_status === 'sending' ? 'Sending…' : row.email_status === 'failed' ? 'Retry email' : 'Send refresh email'}
                                        </button>}
                                        {row.email_status === 'sent' && <span className='ecsuite-recovery-email-sent' title={row.email_sent_at_utc ? `Sent ${formatDate(row.email_sent_at_utc)} UTC` : 'Email sent'}>
                                            <FontAwesomeIcon icon={faCheck} aria-hidden='true' /> Sent
                                        </span>}
                                    </td>
                                </tr>)}
                            </tbody>
                        </table>
                    </div>
                    <div className='ecsuite-recovery-footer px-3 pb-3'>
                        <span>Page {page} of {totalPages}</span>
                        <div className='btn-group' role='group' aria-label='Recovery queue pages'>
                            <button type='button' className='btn btn-outline-secondary btn-sm' onClick={() => changePage(page - 1)} disabled={page <= 1 || isLoading}>Previous</button>
                            <button type='button' className='btn btn-outline-secondary btn-sm' onClick={() => changePage(page + 1)} disabled={page >= totalPages || isLoading}>Next</button>
                        </div>
                    </div>
                </div>
            </div>
            {emailPreview && <div className='ecsuite-recovery-modal-backdrop' role='presentation'>
                <section className='ecsuite-recovery-modal' role='dialog' aria-modal='true' aria-labelledby='send-all-title'>
                    <button type='button' className='ecsuite-recovery-modal-close' onClick={() => setEmailPreview(null)} aria-label='Close send-all review' title='Close'>
                        <FontAwesomeIcon icon={faXmark} aria-hidden='true' />
                    </button>
                    <div className='ecsuite-recovery-modal-icon'><FontAwesomeIcon icon={faTriangleExclamation} aria-hidden='true' /></div>
                    <h2 id='send-all-title'>Send card-refresh emails?</h2>
                    <p>Each eligible customer will receive one email from their website’s configured sender. Duplicate card references for the same customer are combined.</p>
                    <dl className='ecsuite-recovery-review-grid'>
                        <div><dt>Ready to send</dt><dd>{emailPreview.ready_to_send}</dd></div>
                        <div><dt>Already sent</dt><dd>{emailPreview.already_sent}</dd></div>
                        <div><dt>In progress</dt><dd>{emailPreview.in_progress}</dd></div>
                        <div><dt>Skipped references</dt><dd>{emailPreview.ineligible_or_duplicate_references}</dd></div>
                    </dl>
                    <div className='ecsuite-recovery-modal-note'>This sends customer emails. It does not charge cards or retry payments.</div>
                    <div className='ecsuite-recovery-modal-actions'>
                        <button type='button' className='btn btn-outline-secondary' onClick={() => setEmailPreview(null)} disabled={isStartingBatch}>Cancel</button>
                        <button type='button' className='btn btn-primary' onClick={confirmSendAll} disabled={isStartingBatch || emailPreview.ready_to_send === 0}>
                            <FontAwesomeIcon icon={faPaperPlane} aria-hidden='true' />
                            {isStartingBatch ? 'Starting…' : `Send ${emailPreview.ready_to_send} ${emailPreview.ready_to_send === 1 ? 'email' : 'emails'}`}
                        </button>
                    </div>
                </section>
            </div>}
        </div>
    </Container>
}

export default StickyIoCardRecovery
